import { z } from 'zod';
import { announcementAudienceRule } from './announcement-audience.ts';

/**
 * BRAND OPERATIONS — creating a brand, renewing its licence, moving a platform
 * parameter, and composing an announcement.
 *
 * Its own file rather than more of `lists.ts` because everything here writes a
 * row that NOTHING in this platform has ever written at runtime. Until now
 * `platform.brand` had exactly one writer — `packages/db/src/seed.ts:184` — so
 * a brand existed only if a developer had run the seed, `licence_expires_at`
 * could not be moved by anybody, and the dashboard's 60-day licence warning
 * counted down to a date no operator could act on. `platform.setting` was read
 * on six screens and written by nothing. `platform.announcement` could be
 * scheduled and cancelled, and never created.
 *
 * THE DOMAIN IS THE PART TO READ CAREFULLY. It is not a display field; it is
 * the tenancy boundary. See `brandDomain` below.
 */

/* ── The tenancy boundary ─────────────────────────────────────────────────── */

/**
 * A brand's host, as the player portal will resolve it.
 *
 * `services/api/src/modules/player-auth/routes.ts` decides WHICH BRAND A NEW
 * PLAYER BELONGS TO by matching the request's Host header against this column:
 *
 *     SELECT id, ref, name, domain FROM platform.brand
 *      WHERE lower(domain) = $1 AND active
 *
 * with `$1` the host lowercased, de-bracketed and stripped of its port. That
 * makes this string the boundary deciding whose player somebody is, whose
 * licence covers them, whose roster they appear on and whose commission they
 * generate — and there is deliberately no `brandId` anywhere in the player
 * contracts that a caller could use to override it.
 *
 * So the value is not merely validated, it is NORMALISED to exactly the form
 * that lookup can match, and three specific ways of breaking the boundary are
 * refused:
 *
 *   1. CASE. The unique index the schema ships is `brand_domain_key` on the raw
 *      column, which is case-SENSITIVE, while the resolver compares
 *      `lower(domain)`. `FairPlay.ph` and `peryaplay.ph` are therefore two
 *      accepted rows that BOTH match one host, and `rows[0]` then picks a brand
 *      by whatever order the planner happened to return — a tenancy boundary
 *      decided by the query planner. Normalising to lower case here closes the
 *      input path; `packages/db/sql/230_brand_operations.sql` adds a unique
 *      index on `lower(domain)` so the database closes it too.
 *
 *   2. ANYTHING THAT IS NOT A BARE HOST. A scheme, a port, a path, credentials,
 *      a query string or a wildcard can never equal `request.hostname`, so a
 *      brand carrying one is a brand the player portal can never serve — it
 *      would be created, look correct on every screen, and answer
 *      "No brand is served at …" to every player who visited it.
 *
 *   3. A HOST WITH NO DOT. `localhost`, `staging`, and any other single label
 *      are not a public host and are not unique to one operator. The one
 *      environment that legitimately serves a brand at such a host is local
 *      development, and it is already answered by
 *      `PLAYER_BRAND_DOMAIN_OVERRIDE` in `packages/config` — which that package
 *      refuses outright under NODE_ENV=production.
 *
 * Length is 253, matching the column and the DNS limit; each label is capped at
 * 63 for the same reason.
 */
const HOST_LABEL = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
/** Dotted-quad and anything bracketed or colon-bearing: an IP is not a brand host. */
const IPV4_LITERAL = /^\d{1,3}(\.\d{1,3}){3}$/;

export const brandDomain = z
  .string()
  .trim()
  .max(253, 'A host is at most 253 characters.')
  /* Normalised BEFORE the checks, so the value that is validated is byte-for-byte
     the value that will be stored and later compared. A trailing dot is the
     legal absolute form of a name and never appears in a Host header. */
  .transform((raw) => raw.toLowerCase().replace(/\.$/, ''))
  .superRefine((host, ctx) => {
    const reject = (message: string) =>
      ctx.addIssue({ code: 'custom', message });

    if (host.length === 0) {
      reject('Give the brand the host its players will reach it at, such as peryaplay.ph.');
      return;
    }
    if (/[:/\\?#@\s]/.test(host)) {
      reject(
        'Enter the bare host only — no https://, no port, no path. The player portal matches ' +
          'this against the Host header, and a host with any of those in it can never match.',
      );
      return;
    }
    if (host.startsWith('*') || host.includes('*')) {
      reject('A wildcard host cannot be matched to one brand. Name the exact host.');
      return;
    }
    if (IPV4_LITERAL.test(host)) {
      reject('An IP address is not a brand host. Name the domain players will type.');
      return;
    }
    const labels = host.split('.');
    if (labels.length < 2) {
      reject(
        'A brand host needs at least one dot — “peryaplay.ph”, not “peryaplay”. A single label ' +
          'is not a public host and is not unique to one operator.',
      );
      return;
    }
    if (!labels.every((label) => HOST_LABEL.test(label))) {
      reject(
        'Each part of the host is 1–63 letters, digits or hyphens, and cannot start or end ' +
          'with a hyphen.',
      );
    }
  });

/* ── §4 Brand lifecycle ───────────────────────────────────────────────────── */

/**
 * The operator's own name for the brand. Shown on every screen in the console
 * and on the dashboard's brand mix, so it is not allowed to be blank-ish.
 */
export const brandName = z
  .string()
  .trim()
  .min(2, 'Give the brand a name.')
  .max(120, 'A brand name is at most 120 characters.');

/**
 * The regulator's reference for the licence — “PAGCOR-2026-0417”.
 *
 * Optional because `licence_ref` is nullable and a brand may genuinely be
 * pre-licence. It is NOT free-form prose: it is quoted back to a regulator.
 */
const licenceRef = z
  .string()
  .trim()
  .min(3, 'A licence reference is at least 3 characters.')
  .max(64, 'A licence reference is at most 64 characters.')
  .regex(
    /^[A-Za-z0-9][A-Za-z0-9 .\-/]*$/,
    'A licence reference is letters, digits, spaces and - . / only.',
  );

/**
 * When the licence lapses.
 *
 * An INSTANT, not a gaming day. A licence expires at the moment the regulator
 * names, and `packages/db/src/gaming-day.ts` exists for figures that belong to
 * a Manila business day — a licence does not. The dashboard's 60-day warning
 * (`licenceWarnings` in `services/api/src/modules/dashboard/queries.ts`) reads
 * this column with `now() + interval '60 days'`, so a renewal recorded here is
 * the thing that moves the warning.
 */
const licenceExpiresAt = z.iso.datetime();

export const createBrandRequest = z.object({
  name: brandName,
  domain: brandDomain,
  licenceRef: licenceRef.optional(),
  licenceExpiresAt: licenceExpiresAt.optional(),
  /** A brand may be created switched off, so its domain is claimed before launch. */
  active: z.boolean().default(true),
});
export type CreateBrandRequest = z.infer<typeof createBrandRequest>;

/**
 * Editing a brand, INCLUDING renewing its licence.
 *
 * Every field is optional and at least one must be present — a PATCH with an
 * empty body is a request that would write an audit row saying nothing changed.
 * `ref` is absent on purpose: it is the public reference other records quote,
 * and a renamed reference is a broken one.
 */
export const updateBrandRequest = z
  .object({
    name: brandName.optional(),
    domain: brandDomain.optional(),
    licenceRef: licenceRef.optional(),
    licenceExpiresAt: licenceExpiresAt.optional(),
    active: z.boolean().optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'Nothing was changed.',
  });
export type UpdateBrandRequest = z.infer<typeof updateBrandRequest>;

/* ── §4 Platform parameters ───────────────────────────────────────────────── */

/**
 * A parameter's key.
 *
 * Bounded here, and CLOSED at the route: `services/api/src/modules/
 * platform-admin/settings.ts` refuses any key that does not already have a row
 * in `platform.setting`. A settings writer that accepts a new key is a writer
 * that lets a typo create `withdrawal.payout_ratio_hol`, leaving the real
 * parameter untouched while the screen shows a saved change.
 */
export const settingKey = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/, 'A parameter key looks like “group.name”.');

export const writeSettingRequest = z.object({
  key: settingKey,
  /**
   * The new value, as TEXT — which is what the column is. Every reader parses
   * it for itself (`'0.90'`, `'MONDAY'`, `'30'`), and the platform has no type
   * descriptor to validate against, so this contract does not pretend to one.
   * The route refuses a value identical to the current one rather than writing
   * a history row that records no change.
   */
  value: z.string().trim().min(1, 'Give the parameter a value.').max(2000),
  /**
   * Present to write a BRAND OVERRIDE; absent to write the platform-wide row.
   *
   * Only a key whose global row carries `per_brand = true` may have one — the
   * route enforces that against the database, not against a list held here.
   */
  brandId: z.string().min(1).optional(),
});

/*
 * THERE IS DELIBERATELY NO `effectiveAt` HERE.
 *
 * `platform.setting_history.effective_at` is stamped `now()` by the route, and
 * the caller cannot name it. The value is applied to `platform.setting` the
 * instant the transaction commits and every reader picks it up on its next
 * query — there is no scheduler, no pending-change table and nothing that would
 * apply a parameter later. A caller-supplied instant would therefore be a
 * FALSE ENTRY in the configuration log: a future one asserts a delay the
 * platform cannot honour, and a past one asserts the threshold was already
 * moved when a decision was made against the old value. The column means "when
 * this took effect", and the only honest answer is when it did.
 */
export type WriteSettingRequest = z.infer<typeof writeSettingRequest>;

/* ── §19 Announcement composition ─────────────────────────────────────────── */

/**
 * MANDATORY notices ignore marketing opt-out; PROMOTIONAL ones never do.
 *
 * The column comment on `platform.announcement.kind` IS the rule, and two
 * announcements with identical audiences reach different players depending on
 * this one word. A closed set, therefore, and not free text.
 */
export const announcementKind = z.enum(['MANDATORY', 'PROMOTIONAL']);
export type AnnouncementKind = z.infer<typeof announcementKind>;

/**
 * The channels a send would use. `platform.announcement.channels` is a free
 * jsonb array with no CHECK, so this enum is the only thing keeping it a
 * countable vocabulary — the existing screen groups by "In-app + email + push"
 * and free strings make that grouping meaningless.
 */
export const announcementChannel = z.enum(['IN_APP', 'EMAIL', 'PUSH', 'SMS']);
export type AnnouncementChannel = z.infer<typeof announcementChannel>;

/**
 * The copy, one string per locale.
 *
 * BOTH are required to SCHEDULE — `POST …/:id/schedule` refuses a draft missing
 * either, naming which — but neither is required to COMPOSE one: a draft an
 * operator cannot save until the Filipino translation comes back is a draft
 * that gets written in a spreadsheet instead. At least one must be present, or
 * the row carries no copy at all.
 */
export const announcementCopy = z
  .object({
    'en-PH': z.string().trim().min(1).max(2000).optional(),
    'fil-PH': z.string().trim().min(1).max(2000).optional(),
  })
  .refine((copy) => copy['en-PH'] !== undefined || copy['fil-PH'] !== undefined, {
    message: 'Write the copy in at least one locale.',
  });
export type AnnouncementCopy = z.infer<typeof announcementCopy>;

/**
 * WHO IT IS FOR — a rule the platform can count, not a sentence it cannot.
 *
 * This was a free-text `description` for as long as `audience_rule` had no
 * query engine behind it, and the reasoning was sound at the time: a structured
 * predicate implies something executes it, and nothing did. Something does now.
 * `announcementAudienceRule` in `./announcement-audience.ts` is the closed
 * vocabulary, `services/api/src/modules/platform-admin/audience.ts` is the
 * resolver, and the description stored beside the rule is DERIVED from it —
 * so the words on the screen and the set of people who receive the message are
 * the same fact rather than two facts that agree by hand.
 */
export const announcementAudience = announcementAudienceRule;
export type AnnouncementAudience = z.infer<typeof announcementAudience>;

export const createAnnouncementRequest = z.object({
  kind: announcementKind,
  copy: announcementCopy,
  /** At least one, because an announcement with no channel could reach nobody. */
  channels: z.array(announcementChannel).min(1, 'Pick at least one channel.'),
  audience: announcementAudience,
  /** Absent means platform-wide — every brand, and only an unscoped admin may. */
  brandId: z.string().min(1).optional(),
});
export type CreateAnnouncementRequest = z.infer<typeof createAnnouncementRequest>;

/**
 * Rewriting a draft's copy — the missing half of composition.
 *
 * COPY ONLY. Kind, channels, audience and brand are not editable here: a
 * scheduled send is refused for a missing locale, and the operator's whole job
 * at that point is to supply the locale. Changing who it goes to is composing a
 * different announcement, and the route refuses anything that is not a DRAFT.
 */
export const updateAnnouncementCopyRequest = z.object({
  copy: announcementCopy,
});
export type UpdateAnnouncementCopyRequest = z.infer<typeof updateAnnouncementCopyRequest>;
